"use client"

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  getAllAccessibleTasks,
  getArchivedAccessibleTasks,
  getTask as getTaskFn,
  createTask as createTaskFn,
  updateTask as updateTaskFn,
  deleteTask as deleteTaskFn,
  scheduleTask as scheduleTaskFn,
  subscribeToTasks,
} from "@/lib/convex-tasks"
import { toast } from "sonner"
import { useAuthStore } from './auth-store'
import { cacheDebugLog, isStale, type CacheFetchOptions, type CacheStatus } from '@/lib/store-cache'
import type { TaskUpdateInput } from '@/lib/task-update-input'
import { toLocalIsoDate } from '@/lib/date-utils'

/**
 * What kind of work a task is, which decides *when* the planner may place it
 * rather than merely whether it fits.
 */
export type TaskShape = 'deep' | 'shallow' | 'errand' | 'social'

export interface Task {
  id: string
  title: string
  description: string
  priority: "high" | "medium" | "low"
  duration: number
  completed: boolean
  archived: boolean
  scheduled: boolean
  scheduled_time: string | null
  scheduled_date: string | null
  scheduled_time_utc: string | null
  deadline_at: string | null
  reminder_time: number | null
  created_at: string
  updated_at: string
  user_id: string
  individual_project_id: string | null // New field for individual projects
  /** Which part of the user's life this belongs to. Null = unassigned. */
  area_id: string | null
  shape?: TaskShape
  // Recurrence
  recurrence_enabled?: boolean
  recurrence_type?: 'daily' | 'weekly' | 'monthly'
  recurrence_interval?: number | null
  recurrence_weekdays?: number[] | null
  recurrence_end_date?: string | null
  recurrence_anchor?: 'scheduled' | 'completion' | null
  clientState?: {
    phase: 'creating' | 'verifying' | 'failed'
  }
}

export interface CreateTaskDraft {
  id?: string
  title: string
  description?: string
  priority?: "high" | "medium" | "low"
  duration?: number
  individual_project_id?: string | null
  area_id?: string | null
  shape?: TaskShape
  deadline_at?: string | null
  reminder_time?: number | null
  scheduled_date?: string | null
  recurrence_enabled?: boolean
  recurrence_type?: 'daily' | 'weekly' | 'monthly'
  recurrence_interval?: number | null
  recurrence_weekdays?: number[] | null
  recurrence_end_date?: string | null
  recurrence_anchor?: 'scheduled' | 'completion' | null
}

// Translation function types for different scenarios
type TranslationFunctions = {
  // Delete task translations
  task_deleted: string
  task_deleted_description: (title: string) => string
  tasks_deleted: string
  tasks_deleted_description: (count: number) => string
  tasks_restored: string
  tasks_restored_description: (count: number) => string
  undo: string
  task_restored: (title: string) => string
  failed_to_delete_task: string
  unexpected_error: string
  
  // Archive task translations
  task_archived: string
  task_archived_description: (title: string) => string
  task_unarchived: string
  task_unarchived_description: (title: string) => string
  tasks_archived: string
  tasks_archived_description: (count: number) => string
  tasks_unarchived: string
  tasks_unarchived_description: (count: number) => string
  failed_to_archive_task: string
  failed_to_unarchive_task: string
  
  // Schedule task translations
  task_scheduled: string
  task_scheduled_description: (title: string, date: string, time: string) => string
  task_unscheduled: string
  task_unscheduled_description: (title: string) => string
  task: string
  task_failed: string
  
  // Assign task translations
  task_assigned: string
  task_assigned_description: (title: string, assigneeName: string) => string
  task_unassigned: string
  task_unassigned_description: (title: string, assigneeName: string) => string
}

interface TaskState {
  // State
  tasks: Task[]
  archivedTasks: Task[]
  tasksUserId: string | null
  archivedTasksUserId: string | null
  status: CacheStatus
  archivedStatus: CacheStatus
  lastFetchedAt: number | null
  archivedLastFetchedAt: number | null
  isLoading: boolean
  error: string | null
  realtimeStatus: "idle" | "connecting" | "subscribed"
  realtimeSubscriptionId: string | null
  
  // Actions
  refreshTasks: (options?: CacheFetchOptions) => Promise<void>
  refreshArchivedTasks: (options?: CacheFetchOptions) => Promise<void>
  resetTaskState: () => void
  invalidateTasks: () => void
  invalidateArchivedTasks: () => void
  addTask: (task: CreateTaskDraft) => Promise<Task>
  updateTask: (taskId: string, updates: TaskUpdateInput) => Promise<Task>
  deleteTask: (taskId: string, translations: TranslationFunctions) => Promise<void>
  bulkDeleteTasks: (taskIds: string[], translations: TranslationFunctions) => Promise<void>
  archiveTask: (taskId: string, translations: TranslationFunctions) => Promise<void>
  unarchiveTask: (taskId: string, translations: TranslationFunctions) => Promise<void>
  bulkArchiveTasks: (taskIds: string[], translations: TranslationFunctions) => Promise<void>
  scheduleTask: (
    taskId: string,
    date: Date,
    translations: TranslationFunctions,
    locale: string,
    durationOverride?: number,
    recurrence?: {
      recurrence_enabled?: boolean
      recurrence_type?: 'daily' | 'weekly' | 'monthly'
      recurrence_interval?: number | null
      recurrence_weekdays?: number[] | null
      recurrence_anchor?: 'scheduled' | 'completion' | null
      recurrence_end_date?: string | null
      reminder_time?: number | null
      deadline_at?: string | null
    }
  ) => Promise<Task>
  unscheduleTask: (taskId: string, translations: TranslationFunctions) => Promise<Task>
  
  // Internal setters
  setTasks: (tasks: Task[]) => void
  setArchivedTasks: (tasks: Task[]) => void
  setIsLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  dismissTaskCreation: (taskId: string) => void
  setupRealtimeSubscription: () => void
  cleanupRealtimeSubscription: () => void
}

const TASKS_CACHE_TTL_MS = 60_000
const ARCHIVED_TASKS_CACHE_TTL_MS = 60_000

let tasksInFlightPromise: Promise<void> | null = null
let tasksInFlightUserId: string | null = null
let archivedTasksInFlightPromise: Promise<void> | null = null
let archivedTasksInFlightUserId: string | null = null
let taskCollectionsMutationVersion = 0
let convexUnsubscribe: (() => void) | null = null

function noteTaskCollectionsMutation() {
  taskCollectionsMutationVersion += 1
  return taskCollectionsMutationVersion
}

function upsertTaskById(collection: Task[], nextTask: Task): Task[] {
  const existingIndex = collection.findIndex((task) => String(task.id) === String(nextTask.id))
  if (existingIndex === -1) {
    return [nextTask, ...collection]
  }

  const nextCollection = [...collection]
  nextCollection[existingIndex] = nextTask
  return nextCollection
}

function updateTaskById(
  collection: Task[],
  taskId: string,
  updater: (task: Task) => Task,
): Task[] {
  return collection.map((task) =>
    String(task.id) === String(taskId) ? updater(task) : task,
  )
}

function mergeRemoteTasks(remoteTasks: Task[], localTasks: Task[]): Task[] {
  const persistedTaskIds = new Set(remoteTasks.map((task) => String(task.id)))
  const optimisticTasks = localTasks.filter(
    (task) => task.clientState && !persistedTaskIds.has(String(task.id)),
  )

  return [...optimisticTasks, ...remoteTasks]
}


export const useTaskStore = create<TaskState>()(
  devtools(
    (set, get) => ({
      // Initial state
      tasks: [],
      archivedTasks: [],
      tasksUserId: null,
      archivedTasksUserId: null,
      status: "idle",
      archivedStatus: "idle",
      lastFetchedAt: null,
      archivedLastFetchedAt: null,
      isLoading: true,
      error: null,
      realtimeStatus: "idle",
      realtimeSubscriptionId: null,

      // Internal setters
      setTasks: (tasks) =>
        set({ tasks, tasksUserId: useAuthStore.getState().user?.id ?? null }),
      setArchivedTasks: (archivedTasks) =>
        set({
          archivedTasks,
          archivedTasksUserId: useAuthStore.getState().user?.id ?? null,
        }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      dismissTaskCreation: (taskId) =>
        set((state) => ({
          tasks: state.tasks.filter((task) => String(task.id) !== String(taskId)),
        })),
      resetTaskState: () =>
        set({
          tasks: [],
          archivedTasks: [],
          tasksUserId: null,
          archivedTasksUserId: null,
          status: "idle",
          archivedStatus: "idle",
          lastFetchedAt: null,
          archivedLastFetchedAt: null,
          isLoading: false,
          error: null,
          realtimeStatus: "idle",
          realtimeSubscriptionId: null,
        }),

      // Convex pushes the full task list on every relevant write, so there is
      // no event-diffing or polling fallback to maintain here.
      setupRealtimeSubscription: () => {
        const user = useAuthStore.getState().user
        if (!user || convexUnsubscribe) {
          return
        }

        set({ realtimeStatus: "connecting" })

        convexUnsubscribe = subscribeToTasks((tasks) => {
          noteTaskCollectionsMutation()
          cacheDebugLog("tasks", "realtime-event", { count: tasks.length })

          set((state) => ({
            tasks: mergeRemoteTasks(tasks, state.tasks),
            tasksUserId: user.id,
            status: "ready",
            realtimeStatus: "subscribed",
            lastFetchedAt: Date.now(),
            isLoading: false,
            error: null,
          }))
        })

        set({ realtimeSubscriptionId: "convex" })
      },

      cleanupRealtimeSubscription: () => {
        convexUnsubscribe?.()
        convexUnsubscribe = null

        set({
          realtimeSubscriptionId: null,
          realtimeStatus: "idle",
        })
      },

      invalidateTasks: () => {
        cacheDebugLog("tasks", "invalidate")
        set({ lastFetchedAt: null, status: get().tasks.length > 0 ? "ready" : "idle" })
      },

      invalidateArchivedTasks: () => {
        cacheDebugLog("archived-tasks", "invalidate")
        set({
          archivedLastFetchedAt: null,
          archivedStatus: get().archivedTasks.length > 0 ? "ready" : "idle",
        })
      },

      // Refresh tasks from database
      refreshTasks: async (options = {}) => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({
            tasks: [],
            tasksUserId: null,
            isLoading: false,
            status: "idle",
            lastFetchedAt: null,
          })
          return
        }

        const state = get()
        const cacheBelongsToCurrentUser = state.tasksUserId === user.id
        const hasCachedTasks = cacheBelongsToCurrentUser && state.tasks.length > 0
        const stale = isStale(state.lastFetchedAt, TASKS_CACHE_TTL_MS)

        if (!options.force && tasksInFlightPromise && tasksInFlightUserId === user.id) {
          cacheDebugLog("tasks", "dedupe")
          return tasksInFlightPromise
        }

        if (!options.force && hasCachedTasks && !stale) {
          cacheDebugLog("tasks", "hit", { count: state.tasks.length })
          return
        }

        const shouldBackgroundRefresh = hasCachedTasks && !options.force
        cacheDebugLog("tasks", "miss", {
          force: options.force ?? false,
          background: shouldBackgroundRefresh || options.background || false,
        })

        if (!shouldBackgroundRefresh && !options.background) {
          set({ isLoading: true, status: "loading", error: null })
        } else {
          set({ status: "loading", error: null })
        }

        const requestMutationVersion = taskCollectionsMutationVersion
        tasksInFlightPromise = (async () => {
          try {
            const data = await getAllAccessibleTasks()
            const nonArchivedTasks = mergeRemoteTasks(
              data.filter(task => !task.archived),
              get().tasksUserId === user.id ? get().tasks : [],
            )
            const currentUser = useAuthStore.getState().user
            if (!currentUser || currentUser.id !== user.id) {
              cacheDebugLog("tasks", "stale-request", {
                reason: "user-changed",
                originalUserId: user.id,
                currentUserId: currentUser?.id,
              })
              return
            }
            if (requestMutationVersion !== taskCollectionsMutationVersion) {
              cacheDebugLog("tasks", "stale-request", {
                reason: "local-mutation",
                requestMutationVersion,
                currentMutationVersion: taskCollectionsMutationVersion,
              })
              set((state) => ({
                isLoading: false,
                status: state.tasks.length > 0 ? "ready" : "idle",
              }))
              return
            }

            set({
              tasks: nonArchivedTasks,
              tasksUserId: user.id,
              error: null,
              isLoading: false,
              status: "ready",
              lastFetchedAt: Date.now(),
            })
          } catch (err) {
            console.error("Error refreshing tasks:", err)
            const currentUser = useAuthStore.getState().user
            if (!currentUser || currentUser.id !== user.id) {
              cacheDebugLog("tasks", "stale-request-error", {
                reason: "user-changed",
                originalUserId: user.id,
                currentUserId: currentUser?.id,
              })
              return
            }
            if (requestMutationVersion !== taskCollectionsMutationVersion) {
              cacheDebugLog("tasks", "stale-request-error", {
                reason: "local-mutation",
                requestMutationVersion,
                currentMutationVersion: taskCollectionsMutationVersion,
              })
              set((state) => ({
                isLoading: false,
                status: state.tasks.length > 0 ? "ready" : "idle",
              }))
              return
            }
            const error = err instanceof Error ? err.message : "Failed to refresh tasks"
            set({ error, isLoading: false, status: "error" })
            throw err
          } finally {
            tasksInFlightPromise = null
            tasksInFlightUserId = null
          }
        })()
        tasksInFlightUserId = user.id

        return tasksInFlightPromise
      },

      // Refresh archived tasks from database
      refreshArchivedTasks: async (options = {}) => {
        const user = useAuthStore.getState().user
        if (!user) {
          set({
            archivedTasks: [],
            archivedTasksUserId: null,
            archivedStatus: "idle",
            archivedLastFetchedAt: null,
          })
          return
        }

        const state = get()
        const cacheBelongsToCurrentUser = state.archivedTasksUserId === user.id
        const hasCachedTasks = cacheBelongsToCurrentUser && state.archivedTasks.length > 0
        const stale = isStale(state.archivedLastFetchedAt, ARCHIVED_TASKS_CACHE_TTL_MS)

        if (!options.force && archivedTasksInFlightPromise && archivedTasksInFlightUserId === user.id) {
          cacheDebugLog("archived-tasks", "dedupe")
          return archivedTasksInFlightPromise
        }

        if (!options.force && hasCachedTasks && !stale) {
          cacheDebugLog("archived-tasks", "hit", { count: state.archivedTasks.length })
          return
        }

        if (!hasCachedTasks || options.force) {
          set({ archivedStatus: "loading", error: null })
        } else {
          set({ archivedStatus: "loading", error: null })
        }

        const requestMutationVersion = taskCollectionsMutationVersion
        archivedTasksInFlightPromise = (async () => {
          try {
            const archivedTasks = await getArchivedAccessibleTasks()
            const currentUser = useAuthStore.getState().user
            if (!currentUser || currentUser.id !== user.id) {
              cacheDebugLog("archived-tasks", "stale-request", {
                reason: "user-changed",
                originalUserId: user.id,
                currentUserId: currentUser?.id,
              })
              return
            }
            if (requestMutationVersion !== taskCollectionsMutationVersion) {
              cacheDebugLog("archived-tasks", "stale-request", {
                reason: "local-mutation",
                requestMutationVersion,
                currentMutationVersion: taskCollectionsMutationVersion,
              })
              set((state) => ({
                archivedStatus: state.archivedTasks.length > 0 ? "ready" : "idle",
              }))
              return
            }
            set({
              archivedTasks,
              archivedTasksUserId: user.id,
              archivedStatus: "ready",
              archivedLastFetchedAt: Date.now(),
              error: null,
            })
          } catch (err) {
            console.error("Error refreshing archived tasks:", err)
            const currentUser = useAuthStore.getState().user
            if (!currentUser || currentUser.id !== user.id) {
              cacheDebugLog("archived-tasks", "stale-request-error", {
                reason: "user-changed",
                originalUserId: user.id,
                currentUserId: currentUser?.id,
              })
              return
            }
            if (requestMutationVersion !== taskCollectionsMutationVersion) {
              cacheDebugLog("archived-tasks", "stale-request-error", {
                reason: "local-mutation",
                requestMutationVersion,
                currentMutationVersion: taskCollectionsMutationVersion,
              })
              set((state) => ({
                archivedStatus: state.archivedTasks.length > 0 ? "ready" : "idle",
              }))
              return
            }
            set({
              error: err instanceof Error ? err.message : "Failed to refresh archived tasks",
              archivedStatus: "error",
            })
            throw err
          } finally {
            archivedTasksInFlightPromise = null
            archivedTasksInFlightUserId = null
          }
        })()
        archivedTasksInFlightUserId = user.id

        return archivedTasksInFlightPromise
      },

      // Add new task
      addTask: async (task: CreateTaskDraft) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")

        const taskId = task.id ?? crypto.randomUUID()

        const optimisticTask: Task = {
          id: taskId,
          title: task.title || '',
          description: task.description || '',
          priority: task.priority || 'medium',
          duration: task.duration || 30,
          completed: false,
          archived: false,
          scheduled: false,
          scheduled_time: null,
          scheduled_date: null,
          scheduled_time_utc: null,
          deadline_at: task.deadline_at ?? null,
          reminder_time: task.reminder_time ?? null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_id: user.id,
          individual_project_id: task.individual_project_id || null,
          area_id: task.area_id ?? null,
          shape: task.shape,
          recurrence_enabled: task.recurrence_enabled ?? false,
          recurrence_type: task.recurrence_type,
          recurrence_interval: task.recurrence_interval ?? null,
          recurrence_weekdays: task.recurrence_weekdays ?? null,
          recurrence_end_date: task.recurrence_end_date ?? null,
          recurrence_anchor: task.recurrence_anchor ?? null,
          clientState: {
            phase: 'creating',
          },
        }

        noteTaskCollectionsMutation()
        set((state) => ({
          tasks: upsertTaskById(state.tasks, optimisticTask),
        }))

        try {
          const newTask = await createTaskFn({ ...task, id: taskId })

          // The placeholder is dropped by its own id before the real task goes
          // in, because the two ids never match: `taskId` above is a client
          // uuid, while Convex assigns `_id` on insert and `createTask` does not
          // (cannot) send one. Upserting alone therefore found nothing to
          // replace and prepended a second row, leaving a permanent ghost that
          // no refresh could clear — `mergeRemoteTasks` keeps any local task
          // absent from the server, and a client uuid is never on the server.
          set((state) => ({
            tasks: upsertTaskById(
              state.tasks.filter((existing) => String(existing.id) !== String(taskId)),
              newTask,
            ),
          }))

          return newTask
        } catch (err: unknown) {
          console.error("Error in task creation:", err)

          set((state) => ({
            tasks: updateTaskById(state.tasks, taskId, (currentTask) => ({
              ...currentTask,
              clientState: {
                phase: 'verifying',
              },
            })),
          }))

          void (async () => {
            try {
              await get().refreshTasks({ force: true, background: true })
            } catch (refreshError) {
              console.error("Error refreshing tasks after task creation:", refreshError)
            }

            const refreshedTask = get().tasks.find(
              (currentTask) =>
                String(currentTask.id) === String(taskId) && !currentTask.clientState,
            )

            if (refreshedTask) {
              return
            }

            try {
              const verifiedTask = await getTaskFn(taskId)
              if (verifiedTask) {
                set((state) => ({
                  tasks: upsertTaskById(state.tasks, verifiedTask),
                }))
                return
              }
            } catch (verificationError) {
              console.error("Error verifying task creation:", verificationError)
            }

            set((state) => ({
              tasks: updateTaskById(state.tasks, taskId, (currentTask) => ({
                ...currentTask,
                clientState: {
                  phase: 'failed',
                },
              })),
            }))
          })()

          return {
            ...optimisticTask,
            clientState: {
              phase: 'verifying',
            },
          }
        }
      },

      // Update existing task
      updateTask: async (taskId: string, updates: TaskUpdateInput) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")

        // Apply optimistic update
        noteTaskCollectionsMutation()
        set((state) => ({
          tasks: state.tasks.map(task =>
            task.id.toString() === taskId ? { ...task, ...updates } : task
          )
        }))

        try {
          const updatedTask = await updateTaskFn(taskId, updates)
          
          // If no task is returned but no error occurred, the update was likely successful
          // Just refresh the tasks to get the latest state
          if (!updatedTask) {
            await get().refreshTasks()
            // Return the optimistically updated task to prevent UI errors
            return get().tasks.find(t => t.id.toString() === taskId) as Task
          }

          // Update with actual server response if we got one
          set((state) => ({
            tasks: state.tasks.map(task =>
              task.id.toString() === taskId ? updatedTask : task
            )
          }))
          return updatedTask
        } catch (err) {
          // Revert on error
          get().refreshTasks()
          throw err
        }
      },

      // Delete task
      deleteTask: async (id: string, translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (!id || id === 'NaN' || id === 'undefined') {
          throw new Error("Invalid task ID")
        }

        try {
          // Find the task before removing it (check both tasks and archivedTasks)
          let taskToDelete = get().tasks.find(task => task.id.toString() === id)
          let isArchivedTask = false
          
          if (!taskToDelete) {
            taskToDelete = get().archivedTasks.find(task => task.id.toString() === id)
            isArchivedTask = true
          }
          
          if (!taskToDelete) throw new Error("Task not found")

          // Remove task from UI immediately (from the correct array)
          if (isArchivedTask) {
            noteTaskCollectionsMutation()
            set((state) => ({
              archivedTasks: state.archivedTasks.filter(task => task.id.toString() !== id)
            }))
          } else {
            noteTaskCollectionsMutation()
            set((state) => ({
              tasks: state.tasks.filter(task => task.id.toString() !== id)
            }))
          }

          // Create a reference to the timeout so we can cancel it if needed
          let deletionTimeoutId: number | null = null

          // Show toast with undo button
          toast.warning(translations.task_deleted, {
            description: translations.task_deleted_description(taskToDelete.title),
            duration: 5000,
            action: {
              label: translations.undo,
              onClick: () => {
                // Add the task back to the correct list
                if (isArchivedTask) {
                  noteTaskCollectionsMutation()
                  set((state) => ({
                    archivedTasks: [taskToDelete, ...state.archivedTasks]
                  }))
                } else {
                  noteTaskCollectionsMutation()
                  set((state) => ({
                    tasks: [taskToDelete, ...state.tasks]
                  }))
                }
                
                // Cancel the deletion timeout
                if (deletionTimeoutId) {
                  window.clearTimeout(deletionTimeoutId)
                  deletionTimeoutId = null
                }
                
                // Show confirmation toast
                toast.success(translations.task_restored(taskToDelete.title), {
                  duration: 3000,
                })
              }
            }
          })

          // After a delay (to allow for undo), actually delete the task
          deletionTimeoutId = window.setTimeout(async () => {
            try {
              await deleteTaskFn(id)
            } catch (err) {
              console.error("Error completing delayed task deletion:", err)
              // If deletion fails, restore the task to the correct array
              if (isArchivedTask) {
                noteTaskCollectionsMutation()
                set((state) => ({
                  archivedTasks: [taskToDelete, ...state.archivedTasks]
                }))
              } else {
                noteTaskCollectionsMutation()
                set((state) => ({
                  tasks: [taskToDelete, ...state.tasks]
                }))
              }
              toast.error(translations.failed_to_delete_task, {
                description: err instanceof Error ? err.message : translations.unexpected_error,
              })
            }
          }, 5500) // Slightly longer than toast duration
        } catch (err) {
          console.error("Error deleting task:", err)
          
          // If there's an error, refresh the tasks to ensure UI is in sync
          await get().refreshTasks()
          
          // Show error toast
          toast.error(translations.failed_to_delete_task, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Bulk delete tasks (primarily for archived tasks)
      bulkDeleteTasks: async (taskIds: string[], translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (taskIds.length === 0) return

        // Find the tasks to delete (look in archived tasks first, then regular tasks)
        const archivedTasksToDelete = get().archivedTasks.filter(task => taskIds.includes(task.id.toString()))
        const regularTasksToDelete = get().tasks.filter(task => taskIds.includes(task.id.toString()))
        const allTasksToDelete = [...archivedTasksToDelete, ...regularTasksToDelete]

        if (allTasksToDelete.length === 0) {
          throw new Error("No tasks found to delete")
        }

        try {
          // Remove tasks from UI immediately
          noteTaskCollectionsMutation()
          set((state) => ({
            archivedTasks: state.archivedTasks.filter(task => !taskIds.includes(task.id.toString())),
            tasks: state.tasks.filter(task => !taskIds.includes(task.id.toString()))
          }))

          // Create a reference to the timeout so we can cancel it if needed
          let deletionTimeoutId: number | null = null

          // Show toast with undo button
          toast.warning(translations.tasks_deleted, {
            description: translations.tasks_deleted_description(allTasksToDelete.length),
            duration: 5000,
            action: {
              label: translations.undo,
              onClick: () => {
                // Restore tasks to their original arrays
                noteTaskCollectionsMutation()
                set((state) => ({
                  archivedTasks: [...state.archivedTasks, ...archivedTasksToDelete],
                  tasks: [...state.tasks, ...regularTasksToDelete]
                }))
                
                // Cancel the deletion timeout
                if (deletionTimeoutId) {
                  window.clearTimeout(deletionTimeoutId)
                  deletionTimeoutId = null
                }
                
                // Show confirmation toast
                toast.success(translations.tasks_restored, {
                  description: translations.tasks_restored_description(allTasksToDelete.length),
                  duration: 3000,
                })
              }
            }
          })

          // After a delay (to allow for undo), actually delete the tasks
          deletionTimeoutId = window.setTimeout(async () => {
            try {
              await Promise.all(
                taskIds.map(taskId => deleteTaskFn(taskId))
              )
            } catch (err) {
              console.error("Error completing delayed bulk task deletion:", err)
              // If deletion fails, restore the tasks
              noteTaskCollectionsMutation()
              set((state) => ({
                archivedTasks: [...state.archivedTasks, ...archivedTasksToDelete],
                tasks: [...state.tasks, ...regularTasksToDelete]
              }))
              toast.error(translations.failed_to_delete_task, {
                description: err instanceof Error ? err.message : translations.unexpected_error,
              })
            }
          }, 5500) // Slightly longer than toast duration
        } catch (err) {
          console.error("Error bulk deleting tasks:", err)
          
          // If there's an error, refresh both task arrays to ensure UI is in sync
          await get().refreshTasks()
          await get().refreshArchivedTasks()
          
          // Show error toast
          toast.error(translations.failed_to_delete_task, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Archive task
      archiveTask: async (id: string, translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (!id) throw new Error("Task ID is required")

        // Find the task to archive
        const taskToArchive = get().tasks.find(task => task.id.toString() === id)
        if (!taskToArchive) {
          throw new Error("Task not found")
        }

        try {
          // Optimistically remove task from tasks and add to archivedTasks
          const archivedTask = { ...taskToArchive, archived: true, updated_at: new Date().toISOString() }
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: state.tasks.filter(task => task.id.toString() !== id),
            archivedTasks: [...state.archivedTasks, archivedTask]
          }))

          // Update task in database
          await updateTaskFn(id, { archived: true })
          
          // Show success toast with undo button
          let undoTimeoutId: number | null = null
          
          toast.warning(translations.task_archived, {
            description: translations.task_archived_description(taskToArchive.title),
            duration: 5000,
            action: {
              label: translations.undo,
              onClick: async () => {
                // Unarchive the task (move back to tasks from archivedTasks)
                const unarchivedTask = { ...taskToArchive, archived: false, updated_at: new Date().toISOString() }
                noteTaskCollectionsMutation()
                set((state) => ({
                  tasks: [...state.tasks, unarchivedTask],
                  archivedTasks: state.archivedTasks.filter(task => task.id.toString() !== id)
                }))
                
                // Cancel the timeout
                if (undoTimeoutId) {
                  window.clearTimeout(undoTimeoutId)
                  undoTimeoutId = null
                }
                
                try {
                  await updateTaskFn(id, { archived: false })

                  // Mirror the dedicated unarchive flow so any fetch started
                  // before the DB write cannot overwrite the optimistic undo.
                  noteTaskCollectionsMutation()
                  void get().refreshTasks({ force: true, background: true })
                  void get().refreshArchivedTasks({ force: true, background: true })

                  toast.success(translations.task_unarchived_description(taskToArchive.title), {
                    duration: 3000,
                  })
                } catch (err) {
                  console.error("Error unarchiving task:", err)
                  // Revert if unarchive fails (move back to archived)
                  noteTaskCollectionsMutation()
                  set((state) => ({
                    tasks: state.tasks.filter(task => task.id.toString() !== id),
                    archivedTasks: [...state.archivedTasks, archivedTask]
                  }))
                }
              }
            }
          })

        } catch (err) {
          console.error("Error archiving task:", err)
          
          // Revert optimistic update (move back to tasks from archivedTasks)
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: [...state.tasks, taskToArchive],
            archivedTasks: state.archivedTasks.filter(task => task.id.toString() !== id)
          }))
          
          toast.error(translations.failed_to_archive_task, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Unarchive task
      unarchiveTask: async (id: string, translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (!id) throw new Error("Task ID is required")

        // Find the task to unarchive in archivedTasks
        const taskToUnarchive = get().archivedTasks.find(task => task.id.toString() === id)
        if (!taskToUnarchive) {
          throw new Error("Archived task not found")
        }

        try {
          // Optimistically move task from archivedTasks to tasks
          const unarchivedTask = { ...taskToUnarchive, archived: false, updated_at: new Date().toISOString() }
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: [...state.tasks, unarchivedTask],
            archivedTasks: state.archivedTasks.filter(task => task.id.toString() !== id)
          }))

          // Update task in database
          await updateTaskFn(id, { archived: false })

          // Bump mutation version again AFTER the DB write. Any in-flight fetch
          // that captured the pre-write version (N) will now bail out on the
          // version mismatch check, preventing stale server data from wiping
          // the optimistic update. The authoritative refreshes below capture
          // the new version (N+1) and will apply correct post-write server data.
          noteTaskCollectionsMutation()
          void get().refreshTasks({ force: true, background: true })
          void get().refreshArchivedTasks({ force: true, background: true })
          
          // Show success toast
          toast.success(translations.task_unarchived, {
            description: translations.task_unarchived_description(taskToUnarchive.title),
            duration: 3000,
          })

        } catch (err) {
          console.error("Error unarchiving task:", err)
          
          // Revert optimistic update (move back to archivedTasks)
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: state.tasks.filter(task => task.id.toString() !== id),
            archivedTasks: [...state.archivedTasks, taskToUnarchive]
          }))
          
          toast.error(translations.failed_to_unarchive_task, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Bulk archive tasks
      bulkArchiveTasks: async (taskIds: string[], translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (taskIds.length === 0) return

        // Find the tasks to archive before optimistic update
        const tasksToArchive = get().tasks.filter(task => taskIds.includes(task.id.toString()))

        try {
          // Optimistically move tasks from tasks array to archivedTasks array
          const archivedTasks = tasksToArchive.map(task => ({ 
            ...task, 
            archived: true, 
            updated_at: new Date().toISOString() 
          }))
          
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: state.tasks.filter(task => !taskIds.includes(task.id.toString())),
            archivedTasks: [...state.archivedTasks, ...archivedTasks]
          }))

          // Archive each task in the database
          await Promise.all(
            taskIds.map(taskId => updateTaskFn(taskId, { archived: true }))
          )
          
          // Show success toast with undo functionality
          let undoTimeoutId: number | null = null
          
          toast.warning(translations.tasks_archived, {
            description: translations.tasks_archived_description(taskIds.length),
            duration: 5000,
            action: {
              label: translations.undo,
              onClick: async () => {
                // Move tasks back from archivedTasks to tasks
                const unarchivedTasks = tasksToArchive.map(task => ({ 
                  ...task, 
                  archived: false, 
                  updated_at: new Date().toISOString() 
                }))
                
                noteTaskCollectionsMutation()
                set((state) => ({
                  tasks: [...state.tasks, ...unarchivedTasks],
                  archivedTasks: state.archivedTasks.filter(task => !taskIds.includes(task.id.toString()))
                }))
                
                // Cancel the timeout
                if (undoTimeoutId) {
                  window.clearTimeout(undoTimeoutId)
                  undoTimeoutId = null
                }
                
                try {
                  await Promise.all(
                    taskIds.map(taskId => updateTaskFn(taskId, { archived: false }))
                  )

                  // Mirror the dedicated unarchive flow so any fetch started
                  // before the DB writes cannot overwrite the optimistic undo.
                  noteTaskCollectionsMutation()
                  void get().refreshTasks({ force: true, background: true })
                  void get().refreshArchivedTasks({ force: true, background: true })

                  toast.success(`${taskIds.length} ${translations.tasks_unarchived}`, {
                    duration: 3000,
                  })
                } catch (err) {
                  console.error("Error unarchiving tasks:", err)
                  // Revert if unarchive fails - move back to archived
                  noteTaskCollectionsMutation()
                  set((state) => ({
                    tasks: state.tasks.filter(task => !taskIds.includes(task.id.toString())),
                    archivedTasks: [...state.archivedTasks, ...archivedTasks]
                  }))
                }
              }
            }
          })

        } catch (err) {
          console.error("Error bulk archiving tasks:", err)
          
          // Revert optimistic updates - move tasks back from archivedTasks to tasks
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: [...state.tasks, ...tasksToArchive],
            archivedTasks: state.archivedTasks.filter(task => !taskIds.includes(task.id.toString()))
          }))
          
          toast.error(translations.failed_to_archive_task, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Schedule task
      scheduleTask: async (
        taskId: string,
        date: Date,
        translations: TranslationFunctions,
        locale: string,
        durationOverride?: number,
        recurrence?: {
          recurrence_enabled?: boolean
          recurrence_type?: 'daily' | 'weekly' | 'monthly'
          recurrence_interval?: number | null
          recurrence_weekdays?: number[] | null
          recurrence_anchor?: 'scheduled' | 'completion' | null
          recurrence_end_date?: string | null
          reminder_time?: number | null
          deadline_at?: string | null
        }
      ) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (!taskId) throw new Error("Task ID is required")
        if (!date || isNaN(date.getTime())) throw new Error("Invalid scheduling date")

        // Find task first to show optimistic update
        const task = get().tasks.find(t => t.id.toString() === taskId)
        const duration = task?.duration ?? durationOverride
        if (!duration) throw new Error("Task not found and no duration provided")
        const previousTask = task ? { ...task } : null

        // Calculate end time for UI display
        const endTime = new Date(date.getTime() + duration * 60000)
        const scheduledTime = `${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${
          endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }`

        // Optimistically update UI
        noteTaskCollectionsMutation()
        set((state) => ({
          tasks: state.tasks.map(t =>
            t.id.toString() === taskId
              ? {
                  ...t,
                  scheduled: true,
                  scheduled_time: scheduledTime,
                  scheduled_date: toLocalIsoDate(date),
                  scheduled_time_utc: date.toISOString(),
                  duration,
                  deadline_at:
                    recurrence && "deadline_at" in recurrence
                      ? recurrence.deadline_at ?? null
                      : t.deadline_at,
                  reminder_time:
                    recurrence && "reminder_time" in recurrence
                      ? recurrence.reminder_time ?? null
                      : t.reminder_time,
                  ...(recurrence ? {
                    recurrence_enabled: recurrence.recurrence_enabled,
                    recurrence_type: recurrence.recurrence_enabled ? recurrence.recurrence_type : undefined,
                    recurrence_interval: recurrence.recurrence_enabled ? recurrence.recurrence_interval : null,
                    recurrence_weekdays: recurrence.recurrence_enabled ? recurrence.recurrence_weekdays : null,
                    recurrence_anchor: recurrence.recurrence_enabled ? recurrence.recurrence_anchor : undefined,
                    recurrence_end_date: recurrence.recurrence_enabled ? recurrence.recurrence_end_date : null,
                  } : {}),
                }
              : t
          )
        }))

        try {
          const updatedTask = await scheduleTaskFn(taskId, date, recurrence)
          if (!updatedTask) throw new Error("Failed to schedule task")

          // Update with actual server response
          set((state) => ({
            tasks: state.tasks.map(t =>
              t.id.toString() === taskId ? updatedTask : t
            )
          }))

          // Show success toast with proper locale formatting
          const formattedDate = date.toLocaleDateString(locale, { 
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
          const formattedTime = date.toLocaleTimeString(locale, { 
            hour: '2-digit', 
            minute: '2-digit' 
          })
          
          toast.success(translations.task_scheduled, {
            description: translations.task_scheduled_description(
              task?.title ?? translations.task,
              formattedDate,
              formattedTime
            ),
          })

          return updatedTask
        } catch (err) {
          // Revert the optimistic update
          if (previousTask) {
            noteTaskCollectionsMutation()
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id.toString() === taskId ? previousTask : t
              )
            }))
          } else {
            noteTaskCollectionsMutation()
            set((state) => ({
              tasks: state.tasks.map((t) =>
                t.id.toString() === taskId
                  ? { ...t, scheduled: false, scheduled_time: null, scheduled_date: null }
                  : t
              )
            }))
          }

          // Show error toast
          toast.error(translations.task_failed, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })

          throw err
        }
      },

      // Unschedule task (clear scheduled fields)
      unscheduleTask: async (taskId: string, translations: TranslationFunctions) => {
        const user = useAuthStore.getState().user
        if (!user) throw new Error("User not authenticated")
        if (!taskId) throw new Error("Task ID is required")

        // Find task first for optimistic update
        const task = get().tasks.find(t => t.id.toString() === taskId)
        if (!task) throw new Error("Task not found")

        // Optimistic UI update - clear all scheduling and recurrence fields
        noteTaskCollectionsMutation()
        set((state) => ({
          tasks: state.tasks.map(t =>
            t.id.toString() === taskId
              ? { 
                  ...t, 
                  scheduled: false, 
                  scheduled_time: null, 
                  scheduled_date: null, 
                  scheduled_time_utc: null,
                  // Clear recurrence settings when unscheduling
                  recurrence_enabled: false,
                  recurrence_type: undefined,
                  recurrence_interval: null,
                  recurrence_weekdays: null,
                  recurrence_anchor: undefined,
                  recurrence_end_date: null
                }
              : t
          )
        }))

        try {
          const updatedTask = await updateTaskFn(taskId, {
            scheduled: false,
            scheduled_time: null,
            scheduled_date: null,
            scheduled_time_utc: null,
            // Clear recurrence settings when unscheduling
            recurrence_enabled: false,
            recurrence_type: undefined,
            recurrence_interval: null,
            recurrence_weekdays: null,
            recurrence_anchor: undefined,
            recurrence_end_date: null,
          })

          // Ensure state uses server response if available
          if (updatedTask) {
            set((state) => ({
              tasks: state.tasks.map(t =>
                t.id.toString() === taskId ? updatedTask : t
              )
            }))
          }

          toast.success(translations.task_unscheduled, {
            description: translations.task_unscheduled_description(task.title),
          })

          // Return the latest known task
          return (updatedTask || (get().tasks.find(t => t.id.toString() === taskId) as Task))
        } catch (err) {
          // Revert optimistic change on failure
          noteTaskCollectionsMutation()
          set((state) => ({
            tasks: state.tasks.map(t =>
              t.id.toString() === taskId ? task : t
            )
          }))

          toast.error(translations.task_failed, {
            description: err instanceof Error ? err.message : translations.unexpected_error,
          })
          throw err
        }
      },
    }),
    { name: 'task-store' }
  )
)
